import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { io } from "socket.io-client";
import { URL } from "../../store/utils";
import { handleSocketSessionExpiry } from "../../utils/dashboardUtils";
import useLatestToken, { attachTokenOnReconnect } from "../useLatestToken";
import logger from "../../utils/logger";

// Sabit — her render'da yeni referans üretmemek için modül seviyesinde tutulur
const DASHBOARD_TYPES = [
  {
    type: "Firma",
    text: "Firma Tüketimleri",
    graphText: "Yıllık Tüketim Hareketi",
    warn: "Her hangi bir tüketim yok.",
  },
  {
    type: "Test",
    text: "Test Tüketimleri",
    graphText: "Yıllık Tüketim Hareketi",
    warn: "Her hangi bir tüketim yok.",
  },
  {
    type: "Servis",
    text: "Servis Tüketimleri",
    graphText: "Yıllık Tüketim Hareketi",
    warn: "Her hangi bir tüketim yok.",
  },
];

export const useDashboard = (token, permissions = {}) => {
  const { hasToken, tokenRef } = useLatestToken(token);
  const socketRef = useRef(null);
  const [consumptionData, setConsumptionData] = useState(null);
  const [consumptionLoading, setConsumptionLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  // Effect'lerde stale closure yaşamamak için güncel değerlerin ref kopyaları
  const consumptionDataRef = useRef(null);
  const sectionRef = useRef(DASHBOARD_TYPES[0].type);
  // NOT: Cihaz/Firma tüketim kırılımları ayrı istek DEĞİL — dashboardData
  // payload'ının içinde (deviceBreakdown/firmBreakdown) tek yüklemede gelir.

  const [dashboardType, setDashboardType] = useState(DASHBOARD_TYPES[0]);
  const dashboardTypes = DASHBOARD_TYPES;

  const [activeConsumption, setActiveConsumption] = useState("today");

  const [billsData, setBillsData] = useState(null);
  const [devicesInfoData, setDevicesInfoData] = useState(null);
  const [firmsConditionsData, setFirmsConditionsData] = useState(null);
  const [billsLoading, setBillsLoading] = useState(!!permissions.billDetails);
  const [devicesInfoLoading, setDevicesInfoLoading] = useState(!!permissions.deviceStatus);
  const [firmsConditionsLoading, setFirmsConditionsLoading] = useState(!!permissions.firmStatus);
  // Performans rozetleri — tüm periyotlar tek payload'da: { today: {...}, ... }
  const [performanceData, setPerformanceData] = useState(null);

  // Socket.IO event listener'ları için useEffect'ler kaldırıldı

  // Bağlantı kurulur kurulmaz section'dan bağımsız verileri iste.
  // (Tüketim verileri dashboardType effect'inde istenir — burada tekrar edilmez.)
  useEffect(() => {
    if (socketConnected && socketRef.current?.connected) {
      socketRef.current.emit("getDevicesData");
      if (permissions.billDetails) socketRef.current.emit("getBillsData");
      if (permissions.deviceStatus) socketRef.current.emit("getDevicesInfoData");
      if (permissions.firmStatus) socketRef.current.emit("getFirmsConditionsData");
    }
  }, [
    socketConnected,
    permissions.billDetails,
    permissions.deviceStatus,
    permissions.firmStatus,
  ]);

  // Socket.IO bağlantısı - sadece bir kez kurulur
  useEffect(() => {
    // Token yoksa bağlantı kurma
    if (!hasToken) {
      logger.warn("🔌 Token yok, Socket.IO bağlantısı kurulmuyor");
      return;
    }

    // Eğer zaten bağlantı varsa, yeni bağlantı kurma
    if (
      socketRef.current &&
      (socketRef.current.connected || socketRef.current.connecting)
    ) {
      logger.warn("🔌 Socket.IO zaten mevcut, yeni bağlantı kurulmuyor");
      return;
    }

    // Eski bağlantıyı temizle
    if (socketRef.current) {
      logger.warn("🧹 Eski Socket.IO bağlantısı temizleniyor...");
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Socket.IO URL'ini belirle
    const socketUrl = URL.url;

    // Socket.IO bağlantısını kur
    socketRef.current = io(socketUrl, {
      transports: ["websocket", "polling"],
      timeout: 5000,
      query: { token: tokenRef.current },
    });
    attachTokenOnReconnect(socketRef.current, tokenRef); // #142

    // Server, socket auth'u tamamlayıp event handler'larını kurduktan sonra
    // "authenticated" yayar. İlk veri istekleri bu event'e bağlanır — "connect"
    // anında emit edilirse server henüz dinlemediği için istek sessizce düşer
    // ve loading hiç bitmezdi.
    socketRef.current.on("authenticated", () => {
      setSocketConnected(true);
    });

    // Bağlantı hatası - sadece bir kez logla
    socketRef.current.on("connect_error", (error) => {
      logger.error("❌ Socket.IO bağlantı hatası:", error);
      logger.error("❌ Hata detayı:", error.message);
      logger.error("❌ Sunucu erişilebilir değil:", URL.url);
    });

    // Dashboard verisi geldiğinde (real-time güncellemeler)
    socketRef.current.on("dashboardData", (data) => {
      consumptionDataRef.current = data;
      setConsumptionData(data);
      setConsumptionLoading(false);
    });

    // Bills verisi geldiğinde
    socketRef.current.on("billsData", (data) => {
      setBillsData(data);
      setBillsLoading(false);
    });

    // Devices Info verisi geldiğinde (cihaz grupları)
    socketRef.current.on("devicesInfoData", (data) => {
      setDevicesInfoData(data.deviceGroups);
      setDevicesInfoLoading(false);
    });

    // Firma durumları verisi geldiğinde
    socketRef.current.on("firmsConditionsData", (data) => {
      setFirmsConditionsData(data);
      setFirmsConditionsLoading(false);
    });

    // Tüketim performans verisi geldiğinde (tüm periyotlar)
    socketRef.current.on("consumptionPerformanceData", (payload) => {
      setPerformanceData(payload?.performance || {});
    });

    // Hata durumunda — API ile aynı reason: oturum iptali (başka cihaz, süre doldu, şifre değişti) → login'e at
    socketRef.current.on("error", (payload) => {
      const message = payload?.message ?? "Socket hatası";
      const reason = payload?.reason;
      logger.error("❌ Socket.IO hatası:", message, reason ?? "");
      // Sadece oturum/kimlik hatalarında bağlantı durumu düşürülür (server bu
      // durumlarda zaten disconnect eder). Veri hataları ("... alınamadı")
      // bağlantıyı bozmaz — eskiden her hata socketConnected'ı false yapıyor,
      // effect zinciri loading'i tekrar açıp UI'ı sonsuz skeleton'a sokabiliyordu.
      if (reason) {
        setSocketConnected(false);
        if (handleSocketSessionExpiry(reason)) return;
      }
      logger.error(`Socket Hatası: ${message}`);
    });

    // Bağlantı koptuğunda
    socketRef.current.on("disconnect", () => {
      setSocketConnected(false);
    });

    // Cleanup - sadece component unmount olduğunda
    return () => {
      if (socketRef.current) {
        setSocketConnected(false);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [hasToken, tokenRef]); // Yalnız oturum açılıp kapanınca yeniden bağlan (#142)

  // dashboardType değiştiğinde / bağlantı kurulduğunda tüketim verisini iste
  useEffect(() => {
    if (socketConnected && socketRef.current?.connected) {
      const sectionChanged = sectionRef.current !== dashboardType.type;
      sectionRef.current = dashboardType.type;

      // Skeleton sadece ilk yüklemede veya tüketim türü değişince gösterilir.
      // Yeniden bağlanma/tekrar isteklerde eldeki veri ekranda kalır —
      // aksi halde cevap gecikirse dolu ekran sonsuz skeleton'a dönüyordu.
      if (sectionChanged || consumptionDataRef.current == null) {
        setConsumptionLoading(true);
      }

      socketRef.current.emit("getDashboardData", {
        section: dashboardType.type,
      });
      socketRef.current.emit("getConsumptionPerformanceData", {
        section: dashboardType.type,
      });
    }
  }, [dashboardType.type, socketConnected]);

  // Emniyet kemeri: loading açıkken cevap gelmezse isteği periyodik tekrarla —
  // istek kaybolursa (reconnect anı, geçici hata) loading sonsuza kadar dönmesin.
  // Server tarafı debounce + cache'li olduğundan tekrarlar ucuzdur.
  useEffect(() => {
    if (!consumptionLoading || !socketConnected) return undefined;
    const retry = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("getDashboardData", { section: sectionRef.current });
        socketRef.current.emit("getConsumptionPerformanceData", { section: sectionRef.current });
      }
    }, 10000);
    return () => clearInterval(retry);
  }, [consumptionLoading, socketConnected]);

  const handleClickConsumption = useCallback((choice) => {
    setActiveConsumption(choice);
  }, []);

  // monthlyConsumption'ı memoize et - sonsuz döngüyü önlemek için
  const monthlyConsumption = useMemo(() => {
    return consumptionData?.monthlyConsumption || [];
  }, [consumptionData?.monthlyConsumption]);

  const getConsumptionData = () => {
    // Socket.IO'dan gelen canlı veriyi kullan
    const data = consumptionData;
    if (!data) return null;

    switch (activeConsumption) {
      case "today":
        return data.todayConsumption;
      case "yesterday":
        return data.yesterdayConsumption;
      case "thisweek":
        return data.thisWeekConsumption;
      case "thismonth":
        return data.thisMonthConsumption;
      default:
        return data.thisYearConsumption;
    }
  };

  return {
    dashboardTypes,
    dashboardType,
    setDashboardType,
    activeConsumption,
    isLoading: {
      consuments: consumptionLoading,
      bills: billsLoading,
      devicesInfo: devicesInfoLoading,
      firmsConditions: firmsConditionsLoading,
    },
    isFetching: {
      consuments: false,
      bills: false,
      devicesInfo: false,
      firmsConditions: false,
    },
    errors: {
      consuments: null,
      bills: null,
      devicesInfo: null,
      firmsConditions: null,
    },
    data: {
      consuments: consumptionData, // Socket.IO'dan gelen canlı veri
      bills: billsData,
      devicesInfo: devicesInfoData, // Cihaz grupları verisi
      firmsConditions: firmsConditionsData, // Firma durumları verisi
      performance: performanceData, // Performans rozetleri (tüm periyotlar)
    },
    monthlyConsumption, // Memoized monthlyConsumption

    handleClickConsumption,
    getConsumptionData,
  };
};
